import * as Sentry from '@sentry/browser';
import invoke from 'lodash.invoke';
import namespace from '../../../upi/namespace';
import * as PackageInfo from '../../../upi/packageInfo';
import {getLocale} from '@util/LocaleUtils';
import {hasLocalStorage} from '@util/StorageUtils';
import {check, validator} from '@util/ObjectUtils';
import {sanitizeForLogging} from './sanitize';
import {isProviderConsoleBreadcrumb, reportToConsole} from './consoleReporter';
import EventLimiter, {DUPLICATE_WINDOW_MS} from './EventLimiter';
import SentryTransport from './SentryTransport';
import SuppressionSummaryScheduler from './SuppressionSummaryScheduler';
import {rateLimitState} from './RateLimitState';
import {getLoggingConfig} from './config';

// note -> only send logs for production environment
const eventLimiter = new EventLimiter();
let suppressionSummaryScheduler;
const loggingConfig = getLoggingConfig( {
    dsn: process.env.SENTRY_DSN,
    enabledOverride: !!( window.upi && window.upi.conf && window.upi.conf.sentryEnabled ),
    hostname: window.location.hostname,
    loggingEnv: process.env.LOGGING_ENV,
    nodeEnv: process.env.NODE_ENV
} );
const LOGGING_ENABLED = loggingConfig.enabled;
const SUPPRESSION_SUMMARY_TAG = 'duplicate-suppression-summary';

function createSuppressionSummaryEvent () {
    return {
        fingerprint: ['upi', SUPPRESSION_SUMMARY_TAG],
        level: 'info',
        message: 'Client duplicate errors suppressed',
        tags: {upi_logging_event: SUPPRESSION_SUMMARY_TAG}
    };
}

function isSuppressionSummaryEvent ( event ) {
    return !!event && !!event.tags && event.tags.upi_logging_event === SUPPRESSION_SUMMARY_TAG;
}

const hasMessageField = check( validator(
    'argument must contain a "message" property for logging',
    obj => !!obj && obj.message != null && obj.message !== ''
) );

class LoggingProvider {
    constructor () {
        const provider = namespace( '__LOGGING_PROVIDER__' );

        if ( provider.instance ) {
            return provider.instance;
        }

        suppressionSummaryScheduler = new SuppressionSummaryScheduler( {
            getActiveRateLimit: () => rateLimitState.getActive(),
            hasPending: () => eventLimiter.hasPendingSuppressionSummary(),
            queueSummary: () => LoggingProvider.invoke( 'captureEvent', createSuppressionSummaryEvent() ),
            retryMs: DUPLICATE_WINDOW_MS
        } );

        LoggingProvider.invoke( 'init', {
            dsn: loggingConfig.dsn,
            environment: loggingConfig.environment,
            transport: SentryTransport,
            beforeSend: event => {
                const sanitized = sanitizeForLogging( event );
                const activeRateLimit = rateLimitState.getActive();

                if ( activeRateLimit ) {
                    if ( isSuppressionSummaryEvent( sanitized ) ) {
                        suppressionSummaryScheduler.schedule( activeRateLimit.until );
                    }
                    reportToConsole( 'suppressed', {
                        event: sanitized,
                        retryAt: new Date( activeRateLimit.until ).toISOString()
                    }, {detail: activeRateLimit.reason} );
                    return null;
                }

                if ( isSuppressionSummaryEvent( sanitized ) ) {
                    const summary = eventLimiter.takeSuppressionSummary();
                    suppressionSummaryScheduler.cancel();

                    if ( !summary.suppressedCount ) {
                        return null;
                    }

                    sanitized.extra = Object.assign( {}, sanitized.extra, {
                        upi_suppressed_duplicates: summary.suppressedCount,
                        upi_suppressed_fingerprints: summary.fingerprintCount
                    } );
                    reportToConsole( 'queued', sanitized, {level: 'info'} );
                    return sanitized;
                }

                const decision = eventLimiter.evaluate( sanitized );

                if ( !decision.allowed ) {
                    if ( decision.reason === 'duplicate' ) {
                        suppressionSummaryScheduler.schedule( decision.retryAt );
                    }
                    reportToConsole( 'suppressed', sanitized, {detail: decision.reason} );
                    return null;
                }

                if ( decision.suppressedCount ) {
                    sanitized.extra = Object.assign( {}, sanitized.extra, {
                        upi_suppressed_duplicates: decision.suppressedCount
                    } );
                    if ( !eventLimiter.hasPendingSuppressionSummary() ) {
                        suppressionSummaryScheduler.cancel();
                    }
                }

                reportToConsole( 'queued', sanitized );
                return sanitized;
            },
            beforeBreadcrumb: breadcrumb => {
                if ( isProviderConsoleBreadcrumb( breadcrumb ) ) {
                    return null;
                }
                return sanitizeForLogging( breadcrumb );
            }
        } );

        this.configure( {
            tags: {
                upi_frontend: PackageInfo.version,
                page_locale: getLocale(),
                enabled_cookies: window.navigator.cookieEnabled,
                enabled_localstorage: hasLocalStorage()
            }
        } );

        return provider.instance = this;
    }

    static invoke ( cmd, ...rest ) {
        const options = rest != null ? rest : {};
        if ( LOGGING_ENABLED ) {
            return invoke( Sentry, cmd, ...options );
        }
    }

    static reportUnavailable ( payload, detail, level = 'error' ) {
        reportToConsole( 'disabled', payload, {detail, level} );
    }

    static reportInvalid ( method, payload, message ) {
        reportToConsole( 'invalid', {method, message, payload}, {level: 'warn'} );
    }

    /**
     * "command" param in the following format
     * "commandName": [SPREAD_ARRAY_PARAMS]
     *
     * usage:
     * configure({
     *     level: 'debug',
     *     extra: {batter: 0.5},
     *     user: {email: "troy.watt@ultradent.com"},
     *     tag: {one: 'tag only'},
     *     tags: {
     *           version: 123,
     *          'page_local':'en-us'
     *     }
     * })
     *
     *
     *
     */
    configure ( commands ) {
        // console.log( 'logger [configure]' );
        LoggingProvider.invoke( 'configureScope', scope => {
            Object.entries( commands ).forEach(
                ( [prop, args] ) => {
                    if ( prop === 'tags' ) {
                        return this.setTagsForScope( scope, args );
                    }

                    const params = Array.isArray( args ) ? args : [args];
                    const cmd = 'set' + prop.substr( 0, 1 ).toUpperCase() + prop.substr( 1 );
                    return invoke( scope, cmd, ...params );
                }
            )
        } );
    }

    setTagsForScope ( scope, tags = {} ) {
        // map tags object to individual calls of 'setTag' on scope to conform to Sentry api
        Object.entries( tags )
            .forEach( tag => invoke( scope, 'setTag', ...tag ) );
    }

    capture ( err ) {
        if ( err == null ) {
            LoggingProvider.reportInvalid( 'capture', err, 'an error value is required' );
            return;
        }
        if ( !LOGGING_ENABLED ) {
            LoggingProvider.reportUnavailable( err );
            return;
        }
        LoggingProvider.invoke( 'captureException', err );
    }

    addBreadcrumb ( obj ) {
        const errs = hasMessageField( obj );
        if ( errs.length ) {
            LoggingProvider.reportInvalid( 'addBreadcrumb', obj, errs[0] );
            return;
        }
        if ( !LOGGING_ENABLED ) {
            LoggingProvider.reportUnavailable( obj, 'breadcrumb', 'info' );
            return;
        }
        const consoleLevel = obj.level === 'error' ? 'error' : obj.level === 'warning' ? 'warn' : 'info';
        reportToConsole( 'breadcrumb', obj, {level: consoleLevel} );
        LoggingProvider.invoke( 'addBreadcrumb', obj );
    }

    triggerEvent ( obj ) {
        const errs = hasMessageField( obj );
        if ( errs.length ) {
            LoggingProvider.reportInvalid( 'triggerEvent', obj, errs[0] );
            return;
        }
        if ( !LOGGING_ENABLED ) {
            LoggingProvider.reportUnavailable( obj );
            return;
        }
        LoggingProvider.invoke( 'captureEvent', obj );
    }

    sendMessage ( str ) {
        if ( typeof str !== 'string' ) {
            LoggingProvider.reportInvalid( 'sendMessage', str, 'the first argument must be a string' );
            return;
        }
        if ( !LOGGING_ENABLED ) {
            LoggingProvider.reportUnavailable( str );
            return;
        }
        LoggingProvider.invoke( 'captureMessage', str );
    }
}

const logger = new LoggingProvider;
export default logger;
